// src/App.tsx
import React from 'react';
import './App.css';
import {
    BrowserRouter as Router,
    Switch,
    Route,
    Redirect,
    RouteProps,
} from 'react-router-dom';

// Public pages
import Login from './public/Login';
import register from './public/Register'; // keeping your original default export name

// Secure pages (same as your current file)
import Dashboard from './secure/dashboard/Dashboard';
import Users from './secure/users/Users';
import UserCreate from './secure/users/UserCreate';
import UserEdit from './secure/users/UserEdit';
import Roles from './secure/roles/Roles';
import RoleCreate from './secure/roles/RoleCreate';
import RoleEdit from './secure/roles/RoleEdit';
import Surveys from './secure/surveys/Surveys';
import Forms from './secure/forms/Forms';
import Submissions from './secure/submissions/Submissions';
import Profile from './secure/profile/Profile';
import Scoreboard from './secure/scoreboard/Scoreboard';
import KenyaScoreboard from './secure/countries/KenyaScoreboard';
import BurundiScoreboard from './secure/countries/BurundiScoreboard';
import RwandaScoreboard from './secure/countries/RwandaScoreboard';
import SouthSudanScoreboard from './secure/countries/SouthSudanScoreboard';
import TanzaniaScoreboard from './secure/countries/TanzaniaScoreboard';
import UgandaScoreboard from './secure/countries/UgandaScoreboard';
import ProjectsList from './secure/odk/ProjectsList';
import SurveyCreate from "./secure/surveys/SurveyCreate";
import SurveyEdit from "./secure/surveys/SurveyEdit";

// ---------------------------------------------------------------------------
// Auth helpers
// NOTE: Keep this in sync with what you set in Login.tsx (e.g., 'auth' or 'token')
const isAuthenticated = (): boolean => {
    const flag = localStorage.getItem('auth'); // your current approach
    const token = localStorage.getItem('token'); // future-proofing if you add JWTs
    return flag === 'true' || Boolean(token);
};

/**
 * PrivateRoute: blocks rendering of the target component unless authenticated.
 * This prevents the "dashboard flash" because the component never mounts.
 */
const PrivateRoute: React.FC<
    RouteProps & { component: React.ComponentType<any> }
> = ({ component: Component, ...rest }) => (
    <Route
        {...rest}
        render={(props) =>
            isAuthenticated() ? (
                <Component {...props} />
            ) : (
                <Redirect
                    to={{
                        pathname: '/login',
                        state: { from: props.location },
                    }}
                />
            )
        }
    />
);

/**
 * PublicOnlyRoute: if user is already logged in, send them to /dashboard.
 * Useful for /login and /register so authed users don’t see them.
 */
const PublicOnlyRoute: React.FC<
    RouteProps & { component: React.ComponentType<any> }
> = ({ component: Component, ...rest }) => (
    <Route
        {...rest}
        render={(props) =>
            isAuthenticated() ? <Redirect to="/dashboard" /> : <Component {...props} />
        }
    />
);

// ---------------------------------------------------------------------------

function App() {
    return (
        <div className="App">
            <Router>
                <Switch>
                    {/* Root: send users to dashboard if authed, otherwise to login */}
                    <Route
                        path="/"
                        exact
                        render={() =>
                            isAuthenticated() ? <Redirect to="/dashboard" /> : <Redirect to="/login" />
                        }
                    />

                    {/* Public-only routes */}
                    <PublicOnlyRoute path="/login" component={Login} />
                    <PublicOnlyRoute path="/register" component={register} />

                    {/* Private routes (no flash because they never mount when not authed) */}
                    <PrivateRoute path="/dashboard" exact component={Dashboard} />
                    <PrivateRoute path="/profile" exact component={Profile} />

                    <PrivateRoute path="/users" exact component={Users} />
                    <PrivateRoute path="/users/create" component={UserCreate} />
                    <PrivateRoute path="/users/:id/edit" component={UserEdit} />

                    <PrivateRoute path="/roles" exact component={Roles} />
                    <PrivateRoute path="/roles/create" component={RoleCreate} />
                    <PrivateRoute path="/roles/:id/edit" component={RoleEdit} />

                    <PrivateRoute path="/projects" exact component={ProjectsList} />

                    <PrivateRoute path="/surveys" exact component={Surveys} />
                    <Route path={'/surveys/create'} component={SurveyCreate}/>
                    <Route path={'/surveys/:id/edit'} component={SurveyEdit}/>

                    <PrivateRoute path="/forms" exact component={Forms} />
                    <PrivateRoute path="/submissions" exact component={Submissions} />

                    <PrivateRoute path="/scoreboard" exact component={Scoreboard} />

                    <PrivateRoute path="/countries/burundi" exact component={BurundiScoreboard} />
                    <PrivateRoute path="/countries/kenya" exact component={KenyaScoreboard} />
                    <PrivateRoute path="/countries/rwanda" exact component={RwandaScoreboard} />
                    <PrivateRoute path="/countries/south-sudan" exact component={SouthSudanScoreboard} />
                    <PrivateRoute path="/countries/tanzania" exact component={TanzaniaScoreboard} />
                    <PrivateRoute path="/countries/uganda" exact component={UgandaScoreboard} />

                    {/* Fallback: anything unknown -> root */}
                    <Redirect to="/" />
                </Switch>
            </Router>
        </div>
    );
}

export default App;
